import React, { useState } from 'react';
import { ReferenceField, TextField, useNotify, useRefresh } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles';
import styled from 'styled-components';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import { apiPut } from '../../brainApi';
import HomeIcon from '@material-ui/icons/Home';
import DomainIcon from '@material-ui/icons/Domain';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import EventIcon from '@material-ui/icons/Event';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import BuildIcon from '@material-ui/icons/Build';
import { formatAppointmentTime, getZ3PConfigValue } from '../../helper';
import RecurringPlanModal from '../../RecurringAppointments/RecurringPlanModal';
import RescheduleModal from '../../Dispatch/RescheduleModal';
import CancellationModal from '../../Dispatch/CancellationModal';
import StatusChipField from './StatusChipField';
const StyledCancelChip = styled(Box)(() => ({
  borderRadius: 6,
  color: '#B71D18',
  backgroundColor: 'rgba(255, 86, 48, 0.16)',
  padding: '4px 8px',
  marginLeft: 8,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  '&:hover': {
    backgroundColor: 'rgba(255, 86, 48, 0.24)',
  }
}));

const StyledConfirmChip = styled(Box)(() => ({
  borderRadius: 6,
  color: '#118D57',
  backgroundColor: 'rgba(34, 197, 94, 0.16)',
  padding: '4px 8px',
  marginLeft: 8,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  '&:hover': {
    backgroundColor: 'rgba(34, 197, 94, 0.24)',
  }
}));

const StyledEditChip = styled(Box)(() => ({
  borderRadius: 6,
  color: '#000',
  backgroundColor: 'transparent',
  border: '1px solid #e0e0e0',
  padding: '4px 8px',
  marginLeft: 8,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  '&:hover': {
    backgroundColor: '#f5f5f5',
  }
}));

const useStyles = makeStyles(() => ({
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  listColor: {
    color: '#212B36',
    textTransform: 'capitalize',
    marginBottom:8
  },
  iconMargin: {
    marginRight: 16,
  },
  serviceList: {
    color: '#888',
    marginBottom: 5,
  },
  serviceTile: {
    background: '#ebebeb',
    padding: 5,
    borderRadius: 5,
    marginRight: 10,
  },
  listLabel: {
    color: '#637381',
  },
  basicEdit: {
    cursor: 'pointer',
    zIndex: 2,
  },
  listIcon: {
    '& .MuiListItemIcon-root': {
      minWidth: 35,
    },
  },
  pointer: {
    cursor: 'pointer',
  },
  mainBox:{
     marginBottom: 20,
     marginTop:9,
     '& h5': {
      fontWeight:700
     }
  },
  boxMargin: {
    marginBottom:16
  },
  addressColor: {
    color:'#00B8D9',
    textDecoration:'underline',
    marginBottom:8
  },
  editIconStyle:{
    color: '#637381',
    width:16.67,
    height:16.67,
    pointer:'pointer'
  }
}));

const UnifiedViewBasics = ({ setIsEditing, ...rest }) => {
  const { record } = rest;
  const {
    box,
    created_at,
    created_by_employee_id,
    customer,
    customer_address,
    client_location,
    status,
    health_check_url,
    date,
    service_vehicle_name,
    waitlist_note,
    keyosk,
    parking,
    hc_is_complete,
    vehicle,
    location_type,
    st_order_id,
  } = record;
  const { is_fleet_user, fleet_account_name } = customer || {};
  const classes = useStyles();
  const { address_line_1, city, state, postal_code } = customer_address || {};
  const { one_line_human_readable_address, multi_line_human_readable_address } = customer_address || {};

  const { short_name, address } = client_location || {};
  const {
    address_line_1: client_a_l_1,
    city: client_city,
    state: client_state,
    postal_code: client_postal_code,
  } = address || {};

  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleConfirm = () => {
    if (isConfirming) return;
    setIsConfirming(true);
    const endpoint = `/scheduled-work/${record.id}`;
    const body = {
      status: 'confirm',
    };

    apiPut(endpoint, body)
      .then((response) => {
        if (typeof response === 'string' && response.startsWith('Error')) {
          notify(response, 'error');
        } else if (typeof response === 'object' && response.Status === 'Success') {
          notify('Appointment confirmed successfully');
          refresh();
        }
      })
      .catch((error) => {
        notify('Failed to confirm appointment', 'error');
      })
      .finally(() => {
        setIsConfirming(false);
      });
  };

  const created = moment.utc(created_at).local();
  const createdString = created.format('YYYY-MM-DD hh:mm:ss A');
  const createdTimeAgo = created.fromNow();

  const schedulingSystem = getZ3PConfigValue('scheduling_system');
  const timeString = formatAppointmentTime(record);

  return (
    <>
      <CancellationModal
        sw={record}
        showCancellationModal={showCancellationModal}
        setShowCancellationModal={setShowCancellationModal}
      />
      <RecurringPlanModal
        customer={customer}
        vehicle={vehicle}
        sw={record}
        showRecurringModal={showRecurringModal}
        setShowRecurringModal={setShowRecurringModal}
      />
      <RescheduleModal
        open={showRescheduleModal}
        setOpen={setShowRescheduleModal}
        sw={record}
      />
      <Grid container spacing={0}>
        <Grid item xs={10} sm={10} md={10}>
          <Box display="flex" alignItems="center" className={classes.mainBox}>
            <Typography variant="h5" gutterBottom>
              Appointment Details
            </Typography>
            {/*{st_order_id === null ? null : <img
              src="https://zippityinfra.imgix.net/simpletire-icon.png"
              style={{ width: st_order_id === 0 ? 0 : 35, height: st_order_id === 0 ? 0 : 35, paddingRight: 5, paddingLeft: 5 }}
            />}
            {st_order_id === null || st_order_id === 0 ? null :
            <Typography variant="body2" className={classes.listColor}>
              <span className={classes.listLabel}>Order Number: </span>
              {st_order_id}
            </Typography>}*/}
          </Box>
        </Grid>

        <Grid item xs={2} sm={2} md={2}>
          {record.status != 'finished' && record.status != 'cancelled' && (
            <Box className={classes.actionButtons}>
              {record.status == 'scheduled' && (
              <StyledConfirmChip
                onClick={handleConfirm}
                style={{ opacity: isConfirming ? 0.7 : 1, cursor: isConfirming ? 'not-allowed' : 'pointer' }}
              >
                {isConfirming
                  ? <CircularProgress size={12} style={{ color: '#118D57' }} />
                  : <CheckIcon style={{ fontSize: 14 }} />}
                <span>{isConfirming ? 'Confirming...' : 'Confirm'}</span>
              </StyledConfirmChip>
              )}
              {record.status !== 'cancelled' && (
                <StyledCancelChip onClick={() => setShowCancellationModal(true)}>
                  <CancelIcon style={{ fontSize: 14 }} />
                  <span>Cancel</span>
                </StyledCancelChip>
              )}
              <StyledEditChip onClick={() => setIsEditing((prev) => !prev)}>
                <EditIcon style={{ fontSize: 14 }} />
                <span>Reschedule</span>
              </StyledEditChip>
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid item xs={12} sm={12} md={12}>
          <Box>
            <Box display="flex" my={1} className={classes.boxMargin}>
              <Box>
                <svg xmlns="http://www.w3.org/2000/svg" className={classes.iconMargin} width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 17C21 18.0609 20.5786 19.0783 19.8284 19.8284C19.0783 20.5786 18.0609 21 17 21C15.9391 21 14.9217 20.5786 14.1716 19.8284C13.4214 19.0783 13 18.0609 13 17C13 15.9391 13.4214 14.9217 14.1716 14.1716C14.9217 13.4214 15.9391 13 17 13M21 17C21 15.9391 20.5786 14.9217 19.8284 14.1716C19.0783 13.4214 18.0609 13 17 13M21 17H17V13M12 3V7C12 7.26522 12.1054 7.51957 12.2929 7.70711C12.4804 7.89464 12.7348 8 13 8H17M12 3H5C4.46957 3 3.96086 3.21071 3.58579 3.58579C3.21071 3.96086 3 4.46957 3 5V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H11.5M12 3L17 8M17 8V10" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </Box>
              <Box>
                <Typography variant="body2" className={classes.listColor}>
                  <span className={classes.listLabel}>Status</span>
                </Typography>
                <Typography variant="body2" className={classes.listColor}>
                  <StatusChipField status={status} />
                </Typography>
              </Box>
            </Box>
             <Box display="flex" my={1} className={classes.boxMargin}>
            <Box>
              <svg className={classes.iconMargin} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M11.795 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V7C3 6.46957 3.21071 5.96086 3.58579 5.58579C3.96086 5.21071 4.46957 5 5 5H17C17.5304 5 18.0391 5.21071 18.4142 5.58579C18.7893 5.96086 19 6.46957 19 7V11H3M15 3V7M7 3V7M18 16.4961V18.0001L19 19.0001M14 18C14 19.0609 14.4214 20.0783 15.1716 20.8284C15.9217 21.5786 16.9391 22 18 22C19.0609 22 20.0783 21.5786 20.8284 20.8284C21.5786 20.0783 22 19.0609 22 18C22 16.9391 21.5786 15.9217 20.8284 15.1716C20.0783 14.4214 19.0609 14 18 14C16.9391 14 15.9217 14.4214 15.1716 15.1716C14.4214 15.9217 14 16.9391 14 18Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </Box>
            <Box>
              <Typography variant="body1" gutterBottom className={classes.listLabel}>
                Date and Time
              </Typography>
              <Typography variant="body2" className={classes.listColor}>
                {moment(date).format('MM/DD/YYYY')} {timeString}
              </Typography>
            </Box>
          </Box>
            <Box display="flex" my={1} className={classes.boxMargin}>
              <Box>
                <svg className={classes.iconMargin} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M6.99985 9.99985H9.99985V6.99985L6.49985 3.49985C7.61948 2.96513 8.87735 2.79066 10.1003 3.00048C11.3232 3.21029 12.4509 3.79407 13.3283 4.67143C14.2056 5.54878 14.7894 6.67656 14.9992 7.89946C15.209 9.12235 15.0346 10.3802 14.4999 11.4999L20.4999 17.4999C20.8977 17.8977 21.1212 18.4372 21.1212 18.9999C21.1212 19.5625 20.8977 20.102 20.4999 20.4999C20.102 20.8977 19.5625 21.1212 18.9999 21.1212C18.4372 21.1212 17.8977 20.8977 17.4999 20.4999L11.4999 14.4999C10.3802 15.0346 9.12235 15.209 7.89946 14.9992C6.67656 14.7894 5.54878 14.2056 4.67143 13.3283C3.79407 12.4509 3.21029 11.3232 3.00048 10.1003C2.79066 8.87735 2.96513 7.61948 3.49985 6.49985L6.99985 9.99985Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </Box>
              <Box>
                <Typography variant="body2" className={classes.listColor}>
                  <span className={classes.listLabel}>Service bay or vehicle</span>
                </Typography>
                 {/*{location_type === 'shop' ? (
                    <BuildIcon className={classes.iconMargin} />
                  ) : (
                    <LocalShippingIcon className={classes.iconMargin} />
                  )}*/}
                  <Typography variant="body2" className={classes.listColor}>
                    {service_vehicle_name}
                  </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={0}>
        <Grid item xs={12} sm={12} md={6}>
          {one_line_human_readable_address ? (
            <Box display="flex" my={1} className={classes.boxMargin}>
              <Box>
                <svg className={classes.iconMargin}  xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11.8698 21.48C11.3858 21.4493 10.9296 21.2431 10.5868 20.9L6.34279 16.657C5.34665 15.6604 4.63141 14.4183 4.26944 13.0565C3.90746 11.6947 3.9116 10.2615 4.28144 8.90177C4.65127 7.54207 5.37368 6.30418 6.37556 5.31334C7.37745 4.3225 8.62327 3.61386 9.98698 3.25912C11.3507 2.90439 12.7839 2.91613 14.1416 3.29318C15.4993 3.67023 16.7334 4.39919 17.7189 5.40632C18.7044 6.41345 19.4064 7.66302 19.7539 9.02859C20.1014 10.3942 20.082 11.8273 19.6978 13.183M14.9998 19L16.9998 21L20.9998 17M8.99976 11C8.99976 11.7956 9.31583 12.5587 9.87844 13.1213C10.441 13.6839 11.2041 14 11.9998 14C12.7954 14 13.5585 13.6839 14.1211 13.1213C14.6837 12.5587 14.9998 11.7956 14.9998 11C14.9998 10.2044 14.6837 9.44129 14.1211 8.87868C13.5585 8.31607 12.7954 8 11.9998 8C11.2041 8 10.441 8.31607 9.87844 8.87868C9.31583 9.44129 8.99976 10.2044 8.99976 11Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </Box>
              
              <Box>
                <Typography variant="body1" gutterBottom className={classes.listLabel}>
                  Location address
                </Typography>
                <Box>
                  <Typography variant="body2" className={classes.addressColor}>
                    {`${one_line_human_readable_address}`}
                  </Typography>
                  {keyosk && (
                    <Typography variant="body2" className={classes.listColor}>
                      <span className={classes.listLabel}>Keys: </span>
                      <TextField source="keyosk.name" {...rest} />
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box display="flex" my={1} className={classes.boxMargin}>
              <Box>
                <svg className={classes.iconMargin}  xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11.8698 21.48C11.3858 21.4493 10.9296 21.2431 10.5868 20.9L6.34279 16.657C5.34665 15.6604 4.63141 14.4183 4.26944 13.0565C3.90746 11.6947 3.9116 10.2615 4.28144 8.90177C4.65127 7.54207 5.37368 6.30418 6.37556 5.31334C7.37745 4.3225 8.62327 3.61386 9.98698 3.25912C11.3507 2.90439 12.7839 2.91613 14.1416 3.29318C15.4993 3.67023 16.7334 4.39919 17.7189 5.40632C18.7044 6.41345 19.4064 7.66302 19.7539 9.02859C20.1014 10.3942 20.082 11.8273 19.6978 13.183M14.9998 19L16.9998 21L20.9998 17M8.99976 11C8.99976 11.7956 9.31583 12.5587 9.87844 13.1213C10.441 13.6839 11.2041 14 11.9998 14C12.7954 14 13.5585 13.6839 14.1211 13.1213C14.6837 12.5587 14.9998 11.7956 14.9998 11C14.9998 10.2044 14.6837 9.44129 14.1211 8.87868C13.5585 8.31607 12.7954 8 11.9998 8C11.2041 8 10.441 8.31607 9.87844 8.87868C9.31583 9.44129 8.99976 10.2044 8.99976 11Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </Box>
              <Box>
                <Typography variant="body1" gutterBottom className={classes.listLabel}>
                  Location address
                </Typography>
                <Box>
                  <Typography variant="body2" className={classes.listColor}>
                    {short_name}
                  </Typography>
                  <Typography variant="body2" className={classes.addressColor}>
                    {`${client_a_l_1}, ${client_city}, ${client_state}, ${client_postal_code}`}
                  </Typography>
                  {parking && (
                    <Typography variant="body2" className={classes.listColor}>
                      <span className={classes.listLabel}>Parking: </span>
                      <TextField source="parking.name" {...rest} />
                    </Typography>
                  )}
                  {keyosk && (
                    <Typography variant="body2" className={classes.listColor}>
                      <span className={classes.listLabel}>Keys: </span>
                      <TextField source="keyosk.name" {...rest} />
                    </Typography>
                  )}
                  {box && (
                    <Typography variant="body2" className={classes.listColor}>
                      <span className={classes.listLabel}>Box: </span>
                      <TextField source="box" {...rest} />
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
         
          <Box display="none" my={1} className={classes.boxMargin}>
            <Box>
              <svg className={classes.iconMargin} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 7H15M9 11H15M9 15H13M5 5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V5Z" stroke="#637381" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </Box>
            <Box>
              <Typography variant="body1" gutterBottom className={classes.listLabel}>
                Special instructions
              </Typography>
              <Typography variant="body2" className={classes.listColor}>
                
              </Typography>
            </Box>
          </Box>
        </Grid>
        {/*<Grid item xs={12} sm={12} md={6}>
          <Box mt={3}>
            
            {!!is_fleet_user && (
              <Typography variant="body2" className={classes.serviceList}>
                {fleet_account_name ? (
                  <>
                    <span className={classes.listLabel}>
                      Fleet Account Name:{' '}
                    </span>
                    {fleet_account_name}
                  </>
                ) : (
                  <span className={classes.listLabel}>Is Fleet User</span>
                )}
              </Typography>
            )}
            {waitlist_note && (
              <Typography variant="body2" className={classes.serviceList}>
                <span className={classes.listLabel}>
                  This is a waitlist appointment.
                </span>
              </Typography>
            )}
          </Box>
        </Grid>*/}
      </Grid>
    </>
  );
};

export default UnifiedViewBasics;
